"use client"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Menu, X } from "lucide-react"
import { useState, useEffect } from "react"
import Link from "next/link"
import { Wordmark } from "@/app/design_system/Wordmark"
import { cn } from "@/lib/utils"

const MENU_ITEMS = [
  { label: "Our Story", href: "/story" },

  { label: "Explore", href: "/home" },
] as const

interface NavMenuItemsProps {
  className?: string
}

const NavMenuItems = ({ className }: NavMenuItemsProps) => (
  <div className={`flex flex-col md:flex-row gap-1 ${className ?? ""}`}>
    {MENU_ITEMS.map(({ label, href }) => (
      <Link key={label} href={href}>
        <Button variant="ghost" className="w-full md:w-auto">
          {label}
        </Button>
      </Link>
    ))}
  </div>
)

export default function LandingPageNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)

  const toggleMenu = () => setIsMenuOpen((prev) => !prev)

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 0)
    }

    window.addEventListener("scroll", handleScroll)

    // Initial check
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <nav
      className={`transition-all duration-500 fixed top-0 left-0 right-0 z-50 py-3.5 md:py-4 isolate ${hasScrolled ? "bg-muted shadow-md" : "bg-wood"}`}
    >
      <div className="container px-6 m-auto">
        {/* Main navbar row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link href="/">
              <Wordmark
                className="h-6 sm:h-8"
                variant={hasScrolled ? "wood" : "white"}
              />
            </Link>
            <div className="hidden md:flex">
              <NavMenuItems
                className={hasScrolled ? "text-wood" : "text-white"}
              />
            </div>
          </div>
          {/* Desktop auth buttons */}
          <div className="hidden md:flex gap-3">
            <Link href="/login">
              <Button
                className={hasScrolled ? "text-wood" : "text-white"}
                variant="ghost"
              >
                Sign in
              </Button>
            </Link>
            <Link href="/register">
              <Button>Start writing</Button>
            </Link>
          </div>
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            className={cn(
              "px-0 size-9 flex items-center justify-center md:hidden",
              hasScrolled ? "text-wood" : "text-white "
            )}
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
        {/* Mobile menu */}
        {isMenuOpen && (
          <div
            className={cn(
              "md:hidden flex flex-col gap-2.5 pt-4",
              hasScrolled ? "text-muted-foreground" : "text-ivory"
            )}
          >
            <NavMenuItems />
            <Separator className="my-2" />
            <div className="flex flex-col gap-3">
              <Link href="/login">
                <Button variant="ghost" className="w-full">
                  Sign in
                </Button>
              </Link>
              <Link href="/register">
                <Button className="w-full">Sign up</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
